import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Biohazard, ShieldCheck, Info, Heart, AlertTriangle, Menu, X, Search as SearchIcon, ExternalLink, Globe, FileText, ChevronRight, Activity, List } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import { api } from "../lib/api";
import { Company } from "../types";
import { toast } from "sonner";
import { CompanyLogo } from "./CompanyLogo";

interface LayoutProps {
  children: React.ReactNode;
  activeSection: string;
  setActiveSection: (section: string) => void;
  search: string;
  setSearch: (search: string) => void;
  onSelectCompany: (c: Company | number) => void;
  selectedCompany: Company | null;
  stats?: {
    totalCompanies: number;
    criticalThreats: number;
    totalViolations: number;
    totalDeforestation: number;
  };
}

// Canonical URL sanitization helper to guarantee consistent indexing
export function getCanonicalUrl(pathname: string): string {
  let clean = pathname.toLowerCase();
  
  // Strip trailing slashes to guarantee exact matches with our XML sitemap paths
  if (clean.endsWith("/") && clean !== "/") {
    clean = clean.slice(0, -1);
  }
  
  // Clean empty values or standardize paths to support path aliases
  if (clean === "/partners") {
    return "https://biofail.com/sponsor";
  }
  
  return `https://biofail.com${clean === "/" ? "" : clean}`;
}

export function Layout({ children, activeSection, setActiveSection, setSearch, search, onSelectCompany, selectedCompany, stats }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [quickResults, setQuickResults] = React.useState<Company[]>([]);
  const [isSearchFocused, setIsSearchFocused] = React.useState(false);
  const [serverOnline, setServerOnline] = React.useState<boolean | null>(null);
  const location = useLocation();



  React.useEffect(() => {
    const isCustomDomain = typeof window !== "undefined" && 
      !window.location.hostname.includes("run.app") && 
      !window.location.hostname.includes("localhost") && 
      !window.location.hostname.includes("127.0.0.1");

    if (isCustomDomain) {
      setServerOnline(true);
      return;
    }

    fetch("/api/ping")
      .then(res => setServerOnline(res.ok))
      .catch(() => setServerOnline(false));
  }, []);

  const navItems = [
    { id: "index", label: "Registry", icon: FileText, path: "/" },
    { id: "directory", label: "A-Z Directory", icon: List, path: "/directory" },
    { id: "about", label: "About", icon: Info, path: "/about" },
    { id: "sponsor", label: "Partners", icon: Heart, path: "/sponsor" },
  ];

  const getPageTitle = () => {
    const isCompanyPath = !["/", "/about", "/sponsor", "/report", "/methodology", "/directory", "/contact", "/partners"].includes(location.pathname);
    if (isCompanyPath && selectedCompany) {
      return `${selectedCompany.companyName} | Environmental Impact Audit | Biofail`;
    }
    switch (activeSection) {
      case "index": return "Corporate Environment Registry | Biofail Index";
      case "directory": return "A-Z Corporate Environmental Directory | Biofail Registry";
      case "about": return " Biofail | Methodology & Accountability";
      case "sponsor": return "Partners & Supporters | Biofail Index";
      case "report": return "Submit Violation Report | Biofail Index";
      default: return "Biofail | Corporate Impact Index";
    }
  };

  const getPageDescription = () => {
    const isCompanyPath = !["/", "/about", "/sponsor", "/report", "/methodology", "/directory", "/contact", "/partners"].includes(location.pathname);
    if (isCompanyPath && selectedCompany) {
       return `Environmental audit for ${selectedCompany.companyName}. Score: ${selectedCompany.evilScore}%. Tracking ${selectedCompany.violations} violations and ${selectedCompany.totalEmissions}Mt carbon footprint.`;
    }
    const defaultStats = `Tracking ${stats?.totalCompanies || "600+"} corporations and $${stats ? (stats.totalViolations * 0.08).toFixed(1) : "14"}B in environmental fines.`;
    switch (activeSection) {
      case "index": return `Global database of corporate environmental impact. ${defaultStats} Search entities and view destruction profiles.`;
      case "directory": return `Complete interactive alphabetical catalog of all ${stats?.totalCompanies || "600+"} tracked corporations, structured for instant search, carbon metrics tracking, and ESG auditing.`;
      case "about": return "Learn how Biofail sources and verifies ecological data. Our mission is radical transparency in a world of corporate obfuscation.";
      case "sponsor": return "Support standard corporate accountability. Fuel independent environmental auditing, research, and open-source intelligence databases.";
      case "report": return "Submit evidence of corporate environmental crimes. Secure, anonymous, and critical for holding the world's polluters accountable.";
      default: return "The definitive real-time database of global corporate environmental impact, legal violations, and deforestation metrics.";
    }
  };

  React.useEffect(() => {
    if (search.length > 1) {
      api.getCompanies({ search }).then(results => {
        setQuickResults(results.slice(0, 5));
      });
    } else {
      setQuickResults([]);
    }
  }, [search]);

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans selection:bg-destructive selection:text-white">
      {/* System Status Ticker */}
      <div className="bg-stone-50 border-b border-stone-200 text-xs py-2 px-4 flex items-center justify-between font-bold text-stone-500">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Data Connection: Verifiable</div>
          <div className="hidden sm:flex items-center gap-1.5 border-l border-stone-200 pl-4">Registry Database v2.5</div>
        </div>
        <div className="flex items-center gap-4 text-stone-400 font-medium">
           <span className="hidden sm:inline">Public Registry Access</span>
        </div>
      </div>

      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={getPageDescription()} />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={getCanonicalUrl(location.pathname)} />
        <meta property="og:title" content={getPageTitle()} />
        <meta property="og:description" content={getPageDescription()} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      
      <header className="sticky top-0 z-50 w-full border-b border-stone-850 bg-stone-900 text-stone-100 shadow-md">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-6">
          <Link 
            to="/"
            className="flex items-center gap-2.5 cursor-pointer group shrink-0"
          >
            <div className="relative w-8 h-8 flex items-center justify-center shrink-0">
              {/* Premium geometric registry warning mark */}
              <svg viewBox="0 0 100 100" className="w-full h-full select-none" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Clean, thick geometric crimson shield with slightly rounded corners */}
                <path 
                  d="M50 6 L86 24V55C86 73 70 87 50 92C30 87 14 73 14 55V24L50 6Z" 
                  fill="#E31B23" 
                  stroke="#8B0000" 
                  strokeWidth="3.5"
                  strokeLinejoin="round"
                />
                {/* Elegant crisp intersecting white geometric lines for the "Audit / Index" grid */}
                <circle cx="50" cy="48" r="22" stroke="#FFFFFF" strokeWidth="2" opacity="0.4" strokeDasharray="3 3" />
                <path d="M50 16 V80" stroke="#FFFFFF" strokeWidth="1.5" opacity="0.25" strokeDasharray="2 2" />
                <path d="M18 48 H82" stroke="#FFFFFF" strokeWidth="1.5" opacity="0.25" strokeDasharray="2 2" />
                
                {/* Ultra-sharp geometric biohazard core (3 precision intersecting rings) */}
                <g transform="translate(50, 48) scale(0.9) translate(-50, -50)">
                  <circle cx="50" cy="38" r="12" stroke="#FFFFFF" strokeWidth="4" fill="none" />
                  <circle cx="39" cy="57" r="12" stroke="#FFFFFF" strokeWidth="4" fill="none" />
                  <circle cx="61" cy="57" r="12" stroke="#FFFFFF" strokeWidth="4" fill="none" />
                  <circle cx="50" cy="51" r="5" fill="#FFFFFF" />
                  <path d="M50 44 A 7 7 0 0 1 56 49.5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                  <path d="M44.5 49.5 A 7 7 0 0 1 50 44" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                </g>
              </svg>
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-xl font-bold text-white leading-none tracking-tight">Biofail</span>
              <span className="text-[9.5px] uppercase tracking-wider font-extrabold text-stone-400 mt-1 leading-none">Impact Registry</span>
            </div>
          </Link>

          {/* Global Search Bar */}
          <div className="flex-1 max-w-md relative hidden sm:block">
            <div className="relative group">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-300 group-focus-within:text-red-500 transition-colors" />
              <Input 
                aria-label="Search corporations, sectors, or regions"
                placeholder="Search corporations, sectors, or regions..." 
                className="pl-10 h-10 bg-stone-950 border-stone-500 text-stone-100 placeholder-stone-400 focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:border-transparent rounded-md text-sm transition-all shadow-inner"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
              />
            </div>

            {/* Quick Results Dropdown */}
            {isSearchFocused && quickResults.length > 0 && (
              <div className="absolute top-full left-0 w-full mt-1 bg-stone-950 border border-stone-800 shadow-2xl rounded-md overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-[100]">
                <div className="p-3 border-b border-stone-800 bg-stone-950">
                  <span className="text-xs font-bold text-stone-400">Quick Results</span>
                </div>
                {quickResults.map((company) => (
                  <button
                    key={company.id}
                    className="w-full p-3 flex items-center justify-between hover:bg-stone-900/60 transition-colors text-left group"
                    onClick={() => {
                      onSelectCompany(company);
                      setIsSearchFocused(false);
                    }}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-stone-50 group-hover:text-destructive transition-colors">{company.companyName}</span>
                      <span className="text-xs text-stone-400 font-medium">{company.sector}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-destructive">{company.evilScore}</span>
                      <AlertTriangle className="w-3.5 h-3.5 text-destructive group-hover:scale-110 transition-all duration-300" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-2 shrink-0">
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  size="sm"
                  asChild
                  className={cn(
                    "font-semibold h-9 px-3.5 transition-colors",
                    active 
                      ? "bg-stone-800 text-white" 
                      : "text-stone-300 hover:text-white hover:bg-stone-800"
                  )}
                >
                  <Link to={item.path}>
                    {item.label}
                  </Link>
                </Button>
              );
            })}
            <div className="ml-2 pl-2 border-l border-stone-800 flex items-center gap-2">
              <Button 
                variant="destructive" 
                size="sm" 
                asChild
                className="font-bold h-9 px-4 bg-red-600 hover:bg-red-700 text-white"
              >
                <Link to="/report">Report Violation</Link>
              </Button>
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden text-stone-300 hover:text-white hover:bg-stone-800/80"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && (
          <div className="lg:hidden border-b border-stone-800 bg-stone-950 animate-in slide-in-from-top duration-200">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-2">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    asChild
                    className={cn(
                      "justify-start h-12 px-4 font-semibold",
                      active 
                        ? "bg-stone-850 text-white" 
                        : "text-stone-300 hover:text-white hover:bg-stone-900"
                    )}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Link to={item.path}>
                      {item.label}
                    </Link>
                  </Button>
                );
              })}

              <Button 
                variant="destructive" 
                asChild
                className="mt-2 h-12 font-bold bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                <Link to="/report">Report Violation</Link>
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        {children}
      </main>

      <footer className="w-full border-t border-stone-200 bg-stone-50 text-stone-500 py-16">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2 space-y-4">
            <Link to="/" className="flex items-center gap-2 group cursor-pointer">
              <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
                {/* Premium geometric registry warning mark */}
                <svg viewBox="0 0 100 100" className="w-full h-full select-none" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Clean, thick geometric crimson shield with slightly rounded corners */}
                  <path 
                    d="M50 6 L86 24V55C86 73 70 87 50 92C30 87 14 73 14 55V24L50 6Z" 
                    fill="#E31B23" 
                    stroke="#8B0000" 
                    strokeWidth="3.5"
                    strokeLinejoin="round"
                  />
                  {/* Elegant crisp intersecting white geometric lines for the "Audit / Index" grid */}
                  <circle cx="50" cy="48" r="22" stroke="#FFFFFF" strokeWidth="2" opacity="0.4" strokeDasharray="3 3" />
                  <path d="M50 16 V80" stroke="#FFFFFF" strokeWidth="1.5" opacity="0.25" strokeDasharray="2 2" />
                  <path d="M18 48 H82" stroke="#FFFFFF" strokeWidth="1.5" opacity="0.25" strokeDasharray="2 2" />
                  
                  {/* Ultra-sharp geometric biohazard core (3 precision intersecting rings) */}
                  <g transform="translate(50, 48) scale(0.9) translate(-50, -50)">
                    <circle cx="50" cy="38" r="12" stroke="#FFFFFF" strokeWidth="4" fill="none" />
                    <circle cx="39" cy="57" r="12" stroke="#FFFFFF" strokeWidth="4" fill="none" />
                    <circle cx="61" cy="57" r="12" stroke="#FFFFFF" strokeWidth="4" fill="none" />
                    <circle cx="50" cy="51" r="5" fill="#FFFFFF" />
                    <path d="M50 44 A 7 7 0 0 1 56 49.5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                    <path d="M44.5 49.5 A 7 7 0 0 1 50 44" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                  </g>
                </svg>
              </div>
              <span className="text-2xl font-bold text-stone-900 tracking-tight transition-colors group-hover:text-red-600">Biofail Registry</span>
            </Link>
            <p className="text-sm leading-relaxed max-w-sm text-stone-600">
              Dedicated to tracking and verifying corporate environmental impact data through open-source intelligence and public regulatory filings.
            </p>
            <div className="pt-2">
              <Link 
                to="/sponsor" 
                className="inline-flex items-center gap-3.5 px-4 py-2.5 rounded-xl bg-stone-100 hover:bg-stone-200/60 border border-stone-200/80 transition-all hover:scale-[1.02] shadow-sm group"
              >
                <div className="w-8 h-8 rounded-full bg-white p-0.5 flex items-center justify-center overflow-hidden shrink-0 shadow-sm border border-stone-200">
                  <CompanyLogo companyName="Evercove" domain="evercove.com" size="sm" className="border-0 bg-white" />
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-[9px] uppercase tracking-wider text-stone-400 font-extrabold font-mono leading-none mb-1">Global Partner</span>
                  <span className="text-xs font-bold text-stone-800 transition-colors group-hover:text-emerald-600 leading-none">Evercove</span>
                </div>
              </Link>
            </div>
          </div>
          
          <div>
            <h4 className="text-stone-900 font-bold mb-4 text-sm">Public Data</h4>
            <ul className="space-y-2 text-sm text-stone-500">
              <li><Link to="/" className="hover:text-destructive transition-colors underline decoration-stone-200 underline-offset-4">Corporation Index</Link></li>
              <li><Link to="/directory" className="hover:text-destructive transition-colors underline decoration-stone-200 underline-offset-4 font-bold">A-Z Company Directory</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-stone-900 font-bold mb-4 text-sm">Project Info</h4>
            <ul className="space-y-2 text-sm text-stone-500">
              <li><Link to="/about" className="hover:text-destructive transition-colors underline decoration-stone-200 underline-offset-4">Methodology</Link></li>
              <li><Link to="/sponsor" className="hover:text-destructive transition-colors underline decoration-stone-200 underline-offset-4">Support Registry</Link></li>
              <li><Link to="/report" className="hover:text-destructive transition-colors underline decoration-stone-200 underline-offset-4">Data Correction</Link></li>
              <li><Link to="/terms" className="hover:text-destructive transition-colors underline text-stone-600 decoration-stone-200 underline-offset-4">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-destructive transition-colors underline text-stone-600 decoration-stone-200 underline-offset-4">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 mt-12 pt-8 border-t border-stone-200/60 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-stone-400">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>© {new Date().getFullYear()} Biofail Index. All rights reserved.</span>
            <span className="text-stone-300">|</span>
            <Link to="/terms" className="hover:text-red-600 transition-colors font-semibold text-stone-500 hover:underline">Terms</Link>
            <span className="text-stone-300">|</span>
            <Link to="/privacy" className="hover:text-red-600 transition-colors font-semibold text-stone-500 hover:underline">Privacy</Link>
            <span className="text-stone-300">|</span>
            <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-red-600 transition-colors font-medium decoration-stone-200 hover:underline">Sitemap</a>
            <span className="text-stone-300">|</span>
            <a href="/robots.txt" target="_blank" rel="noopener noreferrer" className="hover:text-red-600 transition-colors font-medium decoration-stone-200 hover:underline">Robots</a>
            <span className="text-stone-300">|</span>
            <a href="/ai.txt" target="_blank" rel="noopener noreferrer" className="hover:text-red-600 transition-colors font-medium decoration-stone-200 hover:underline">AI Crawlers Guide (ai.txt)</a>
          </p>
          <p>
            Website by{" "}
            <a 
              href="https://feelize.com/start" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-red-600 font-semibold underline underline-offset-2 decoration-stone-200 hover:decoration-red-500 transition-colors"
            >
              feelize
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
